import React, { useState } from 'react'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import logo from '../../Components/images/logo.png'
import { FormControl, FormLabel, Radio, RadioGroup, Autocomplete, Link } from '@mui/material'

import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
// Fix lint error
import { makePostRequest, makeGetRequest } from 'utils/api'
import urls from 'utils/urls'
import mailicon from '../../Components/images/mail.png'
import LanguageSelector from 'Components/LanguageSelector' // LAP-733 (GE022)
import { LoadingButton } from '@mui/lab'
import i18n, { SUPPORTED_LANGUAGES } from '../../i18n' // LAP-752 — propagate UI language to backend at signup

export default function Register() {
  let navigate = useNavigate()
  const { t, i18n: i18nInstance } = useTranslation('layout') // LAP-733 (GE022)
  const [loading, setLoading] = React.useState(false)
  const [mailSend, setMailSend] = useState(false)
  const [mailExit, setMailExit] = useState(false)
  // LAP-733 (GE022) — store the *key* of the email error rather than the
  // localised string so that switching language re-renders the right copy.
  const [emailErrorKey, setEmailErrorKey] = useState('register.emailExists')

  const [countries, setCountries] = useState([])
  const [selectedCountry, setSelectedCountry] = useState(null)
  
  const [tosPrivacyAccepted, setTosPrivacyAccepted] = useState(false)
  const [smsMarketingAccepted, setSmsMarketingAccepted] = useState(false)
  const [consentError, setConsentError] = useState(false)
  const [, setPolicyVersions] = useState(null)

  React.useEffect(() => {
    getCountry()
    getPolicyVersions()
  }, [])

  const getCountry = async () => {
    const res = await makeGetRequest('/api/base/get-country/', {}, false)
    if (res.status === 200) {
      let data = []
      res.data.forEach((e) => {
        data.push({ ...e, label: `${e.CountryCode} | ${e.Designation}` })
      })
      setCountries(data)
    }
  }

  const getPolicyVersions = async () => {
    try {
      const res = await makeGetRequest('/api/legal/policies/current', {}, false)
      if (res.status === 200) {
        setPolicyVersions(res.data)
      }
    } catch (error) {
      console.error('Error fetching policy versions:', error)
    }
  }

  const openPolicy = (policyType) => {
    window.open(`/legal/policies/${policyType}`, '_blank')
  }

  const handleSubmit = async (event) => {
    setLoading(true)
    event.preventDefault()
    
    // Validate consent checkbox
    if (!tosPrivacyAccepted) {
      setConsentError(true)
      setLoading(false)
      return
    }
    
    setConsentError(false)
    
    const data = new FormData(event.currentTarget)
    // LAP-752 — capture the language the user picked on the auth page so
    // the backend stores it on the new user. Without this, every signup
    // landed on user.language='fr' and reverted the UI to FR after the
    // post-verification login (the i18n sync in NavBar overwrites the
    // localStorage value with whatever the backend returns).
    const currentLang = (i18nInstance?.language || i18n.language || 'fr').slice(0, 2)
    const language = SUPPORTED_LANGUAGES.includes(currentLang) ? currentLang : 'fr'

    const payload = {
      nom: data.get('nom'),
      prenom: data.get('prenom'),
      email: data.get('email'),
      password: data.get('password'),
      numero_de_mobile: data.get('numero_de_mobile'),
      date_de_naissance: data.get('date_de_naissance'),
      genre: data.get('genre'),
      pays_id: selectedCountry ? selectedCountry.CountryCode : null,
      tos_privacy_accepted: tosPrivacyAccepted,
      sms_marketing_accepted: smsMarketingAccepted,
      language,
    }

    const response = await makePostRequest(urls.register, payload, {}, false)
    if (response.status === 200) {
      // navigate('/login')
      setMailSend(true)
    } else if (response.status === 401) {
      setLoading(false)
      setMailExit(true)
      setEmailErrorKey('register.emailExists')
    } else if (response.status === 402) {
      setLoading(false)
      setEmailErrorKey('register.emailInvalid')
      setMailExit(true)
    } else {
      setLoading(false)
    }
  }

  return (
    // <ThemeProvider theme={defaultTheme}>
    <Box component="main" sx={pageSx}>
      {/* <CssBaseline /> */}
      <Container maxWidth="sm" sx={containerSx}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar> */}
        {/* LAP-733 (GE022) — language picker, no persist (unauthenticated) */}
        <Box sx={langPickerWrapSx}>
          <LanguageSelector compact disablePersist />
        </Box>
        <Box sx={logoWrapSx}>
          <img src={logo} alt="Life" style={{ width: 118, height: 'auto', display: 'block' }} />{' '}
        </Box>
        {mailSend ? (
          <Box
            sx={successCardSx}
          >
            <Box sx={{ mb: 5 }}>
              <img src={mailicon} alt="" />{' '}
            </Box>
            <Box sx={{ fontWeight: '700', fontSize: 14, textAlign: 'center', mb: 3 }}>
              {t('auth.register.successMessage')}
            </Box>
            <Box
              onClick={() => {
                setMailSend(false)
                navigate('/login')
              }}
              mt={1}
              sx={{ color: '#6ea5f7', textDecoration: 'underline', cursor: 'pointer' }}
            >
              {t('auth.register.signIn')}{' '}
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={formCardSx}>
            <Typography component="h1" variant="h4" sx={titleSx}>
              {t('auth.register.title')}
            </Typography>
            <Typography component="p" sx={subtitleSx}>
              {t('auth.register.subtitle')}
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
              <Grid container spacing={2.25}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    autoComplete="nom"
                    name="nom"
                    required
                    fullWidth
                    id="nom"
                    label={t('auth.register.lastName')}
                    size="small"
                    variant="outlined"
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    id="prenom"
                    label={t('auth.register.firstName')}
                    name="prenom"
                    autoComplete="prenom"
                    size="small"
                    variant="outlined"
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    // error
                    // helperText="error"
                    required
                    fullWidth
                    id="email"
                    label={t('auth.register.email')}
                    name="email"
                    autoComplete="n-email"
                    size="small"
                    variant="outlined"
                    sx={textFieldSx}
                    error={mailExit}
                    helperText={mailExit ? t(`auth.${emailErrorKey}`) : ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="numero_de_mobile"
                    label={t('auth.register.mobile')}
                    name="numero_de_mobile"
                    autoComplete="numero_de_mobile"
                    size="small"
                    variant="outlined"
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="password"
                    label={t('auth.register.newPassword')}
                    type="password"
                    id="password"
                    autoComplete="n-password"
                    size="small"
                    variant="outlined"
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    disablePortal
                    id="combo-box-demo"
                    options={countries}
                    sx={autocompleteSx}
                    onChange={(event, newValue) => {
                      setSelectedCountry(newValue)
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('auth.register.country')}
                        variant="outlined"
                        required
                        sx={textFieldSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="date_de_naissance"
                    label={t('auth.register.birthDate')}
                    type="date"
                    id="date_de_naissance"
                    autoComplete="date_de_naissance"
                    size="small"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl sx={radioControlSx} required>
                    <FormLabel id="demo-row-radio-buttons-group-label" sx={sectionLabelSx}>
                      {t('auth.register.gender')}
                    </FormLabel>
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="genre"
                      className="group"
                      sx={radioGroupSx}
                    >
                      <FormControlLabel value="female" control={<Radio />} label={t('auth.register.genderFemale')} />
                      <FormControlLabel value="male" control={<Radio />} label={t('auth.register.genderMale')} />
                      <FormControlLabel value="other" control={<Radio />} label={t('auth.register.genderOther')} />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={consentError ? consentErrorSx : consentBlockSx}
                >
                  <FormControlLabel
                    sx={checkboxLabelSx}
                    control={
                      <Checkbox
                        checked={tosPrivacyAccepted}
                        onChange={(e) => setTosPrivacyAccepted(e.target.checked)}
                        sx={checkboxSx}
                      />
                    }
                    label={
                      <Box sx={legalTextSx}>
                        {t('auth.register.consentRead')}{' '}
                        <Link
                          component="button"
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            openPolicy('TERMS')
                          }}
                          sx={linkSx}
                        >
                          {t('auth.register.termsLink')}
                        </Link>
                        {' '}{t('auth.register.and')}{' '}
                        <Link
                          component="button"
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            openPolicy('PRIVACY')
                          }}
                          sx={linkSx}
                        >
                          {t('auth.register.privacyLink')}
                        </Link>
                        .
                      </Box>
                    }
                  />
                  {consentError && (
                    <Typography sx={{ color: 'red', fontSize: '12px', mt: 1 }}>
                      {t('auth.register.consentRequired')}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    sx={checkboxLabelSx}
                    control={
                      <Checkbox
                        checked={smsMarketingAccepted}
                        onChange={(e) => setSmsMarketingAccepted(e.target.checked)}
                        sx={checkboxSx}
                      />
                    }
                    label={
                      <Box sx={legalTextSx}>
                        {t('auth.register.smsOptIn')}
                      </Box>
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={cookieTextSx}
                >
                  {t('auth.register.cookiesNotice')}{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      openPolicy('COOKIES')
                    }}
                    sx={linkSx}
                  >
                    {t('auth.register.cookiesLink')}
                  </Link>
                  .
                </Grid>
              </Grid>
              {/* <Button
                type="submit"
                fullWidth
                size="large"
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                
              >
                S'inscrire
              </Button> */}
              <LoadingButton
                loading={loading}
                loadingPosition="start"
                type="submit"
                fullWidth
                size="large"
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.45,
                  borderRadius: 2,
                  fontSize: 16,
                  fontWeight: 800,
                  letterSpacing: 0,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #2d8cff 0%, #1d6fd1 100%)',
                  boxShadow: '0 18px 34px rgba(30, 126, 230, 0.32)',
                  ':hover': {
                    background: 'linear-gradient(135deg, #4aa0ff 0%, #2379dc 100%)',
                    boxShadow: '0 20px 40px rgba(30, 126, 230, 0.4)',
                  },
                  '&.Mui-disabled': { color: 'white', backgroundColor: '#5a5a5a' },
                }}
              >
                <span>{t('auth.register.submit')}</span>
              </LoadingButton>
              <Grid container justifyContent="flex-end">
                <Grid
                  item
                  color={'white'}
                  sx={{
                    width: '100%',
                    textAlign: 'center',
                    mt: 1,
                    color: 'rgba(255,255,255,0.82)',
                    fontSize: 15,
                    ':hover': { cursor: 'pointer' },
                    span: {
                      fontWeight: '700',
                      color: '#4ab2ff !important',
                      textDecoration: 'underline !important',
                    },
                  }}
                  onClick={() => navigate('/login')}
                >
                  {t('auth.register.haveAccount')} <span>{t('auth.register.signIn')}</span>
                </Grid>
              </Grid>
            </Box>
            </Box>
          </>
        )}
      </Box>
      {/* <Copyright sx={{ mt: 5 }} /> */}
      </Container>
    </Box>
    // </ThemeProvider>
  )
}

const pageSx = {
  minHeight: '100vh',
  background:
    'radial-gradient(circle at 18% 8%, rgba(56, 132, 255, 0.2) 0, rgba(56, 132, 255, 0) 28%), radial-gradient(circle at 82% 20%, rgba(23, 210, 188, 0.13) 0, rgba(23, 210, 188, 0) 24%), #01031d',
  color: 'white',
  overflowX: 'hidden',
}

const containerSx = {
  py: { xs: 4, sm: 7, md: 9 },
  px: { xs: 2.25, sm: 3 },
}

const logoWrapSx = {
  mb: { xs: 3, sm: 4 },
  filter: 'drop-shadow(0 16px 28px rgba(0, 0, 0, 0.35))',
}

// LAP-733 (GE022) — top-right language picker styling for the unauthenticated
// signup page. Mirrors the Login header's styling so both pages feel coherent.
const langPickerWrapSx = {
  alignSelf: 'flex-end',
  mb: 2,
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    borderRadius: 2,
    '& fieldset': { border: 'none' },
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.14)' },
    '&.Mui-focused': { backgroundColor: 'rgba(255, 255, 255, 0.16)' },
  },
  '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.78)' },
}

const formCardSx = {
  width: '100%',
  maxWidth: 640,
  p: { xs: 2.5, sm: 4 },
  borderRadius: 4,
  border: '1px solid rgba(255, 255, 255, 0.12)',
  background: 'rgba(5, 10, 38, 0.72)',
  boxShadow: '0 30px 80px rgba(0, 0, 0, 0.42)',
  backdropFilter: 'blur(18px)',
}

const successCardSx = {
  ...formCardSx,
  color: 'white',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
}

const titleSx = {
  color: 'white',
  fontWeight: 800,
  textAlign: 'center',
  letterSpacing: 0,
}

const subtitleSx = {
  color: 'rgba(255,255,255,0.78)',
  fontSize: 18,
  fontWeight: 600,
  textAlign: 'center',
  mt: 0.75,
}

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 50,
    borderRadius: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.96)',
    transition: 'box-shadow 160ms ease, background-color 160ms ease',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.22)' },
    '&:hover fieldset': { borderColor: '#79b7ff' },
    '&.Mui-focused': {
      backgroundColor: '#fff',
      boxShadow: '0 0 0 4px rgba(74, 178, 255, 0.18)',
    },
    '&.Mui-focused fieldset': { borderColor: '#4ab2ff', borderWidth: 1 },
    '&.Mui-error fieldset': { borderColor: '#ff6b6b' },
  },
  '& .MuiInputBase-input': {
    color: '#070a1d',
    fontSize: 16,
    fontWeight: 600,
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(7, 10, 29, 0.72)',
    fontWeight: 700,
  },
  '& .MuiInputLabel-shrink': {
    px: 0.75,
    borderRadius: 1,
    backgroundColor: '#f7f8fc',
    transform: 'translate(12px, -8px) scale(0.75)',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#1d6fd1' },
  '& .MuiInputLabel-root.Mui-error': { color: '#ff6b6b' },
  '& .MuiFormHelperText-root': {
    mx: 0,
    color: '#ff8b8b',
    fontWeight: 600,
  },
}

const autocompleteSx = {
  width: '100%',
  '& .MuiAutocomplete-popupIndicator': { color: '#070a1d' },
  '& .MuiAutocomplete-clearIndicator': { color: '#070a1d' },
}

const sectionLabelSx = {
  color: 'white',
  fontWeight: 800,
  fontSize: 16,
  mb: 1,
  '&.Mui-focused': { color: 'white' },
}

const radioControlSx = {
  width: '100%',
  '.Mui-focused': { color: 'white !important' },
}

const radioGroupSx = {
  color: 'white',
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
  gap: { xs: 0.5, sm: 1 },
  '.MuiFormControlLabel-root': {
    m: 0,
    px: 1.25,
    py: 0.75,
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  '.MuiRadio-root': { color: 'rgba(255,255,255,0.86)' },
  '.MuiRadio-root.Mui-checked': { color: '#4ab2ff !important' },
  '.MuiFormControlLabel-label': { fontWeight: 700 },
}

const consentBlockSx = {
  color: 'white',
}

const consentErrorSx = {
  color: 'white',
  border: '1px solid rgba(255, 107, 107, 0.72)',
  backgroundColor: 'rgba(255, 107, 107, 0.1)',
  padding: '10px',
  borderRadius: 2,
}

const checkboxLabelSx = {
  alignItems: 'flex-start',
  m: 0,
  '.MuiFormControlLabel-label': { width: '100%' },
}

const checkboxSx = {
  color: 'rgba(255,255,255,0.86)',
  mt: -0.75,
  ml: -1,
  '&.Mui-checked': { color: '#4ab2ff' },
}

const legalTextSx = {
  fontSize: 14.5,
  lineHeight: 1.55,
  color: 'rgba(255,255,255,0.88)',
  fontWeight: 600,
}

const cookieTextSx = {
  color: 'rgba(255,255,255,0.76)',
  fontSize: 13.5,
  lineHeight: 1.6,
  mt: 0.5,
}

const linkSx = {
  color: '#5bbcff',
  textDecoration: 'none',
  fontWeight: 800,
  p: 0,
  verticalAlign: 'baseline',
  ':hover': {
    color: '#8fd2ff',
    textDecoration: 'underline',
  },
}
